import { Link } from 'react-router-dom';
import SEO from '../../components/seo/SEO';
import { useLanguageLink } from '../../hooks/useLanguageLink';

export default function NotFoundPage() {
  const { createLink } = useLanguageLink();

  return (
    <>
      {/* SEO Meta Tags */}
      <SEO
        title="Page Not Found - 404 Error"
        description="The page you're looking for cannot be found. Return to Axetra homepage or browse our site navigation."
        noindex={true}
      />
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="text-center max-w-2xl">
        {/* 404 Animation */}
        <div className="mb-8 relative">
          <div className="text-9xl md:text-[200px] font-bold opacity-10 text-primary-600">
            404
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-32 h-32 md:w-48 md:h-48 text-primary-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Page Not Found
        </h1>
        <p className="text-lg md:text-xl text-gray-600 mb-8">
          Oops! The page you're looking for seems to have wandered off into the void.
          Don't worry though, we can help you find your way back.
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link
            to={createLink('')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Back to Home
          </Link>
          <Link
            to={createLink('contact')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 border-2 border-primary-200 rounded-lg font-semibold hover:border-primary-400 hover:bg-primary-50 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Contact Support
          </Link>
        </div>

        {/* Quick Links */}
        <div className="border-t border-gray-200 pt-8">
          <p className="text-sm text-gray-600 mb-4 font-semibold">Popular Pages:</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <Link
              to={createLink('pricing')}
              className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
            >
              Pricing
            </Link>
            <span className="text-gray-300">•</span>
            <Link
              to={createLink('faq')}
              className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
            >
              FAQ
            </Link>
            <span className="text-gray-300">•</span>
            <Link
              to={createLink('about')}
              className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
            >
              About Us
            </Link>
            <span className="text-gray-300">•</span>
            <Link
              to={createLink('register')}
              className="text-primary-600 hover:text-primary-700 font-medium hover:underline"
            >
              Sign Up
            </Link>
          </div>
        </div>

        {/* Fun Error Codes */}
        <div className="mt-12 p-4 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200">
          <p className="text-xs text-gray-500">
            Error Code: 404 • Status: NOT_FOUND • Server: Axetra-WEB-01
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
